/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useTelephonyDomainsColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for defining telephony domains columns with custom renderers.
────────────────────────────────────────────────────────────────*/

import { useMemo, useCallback } from 'react';
import { Shield } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import type { Domain } from '../types';

export function useTelephonyDomainsColumns() {
  const { t, i18n, refreshKey } = useI18n();

  const availableColumns = useMemo(() => {
    return [
      { key: 'domainName', label: t('telephonyDomains.domainName'), visible: true, sortable: true },
      { key: 'domainDescription', label: t('telephonyDomains.domainDescription'), visible: true, sortable: true },
      { key: 'serviceType', label: t('telephonyDomains.serviceType', 'Service Type'), visible: true, sortable: true },
      { key: 'tenantName', label: t('telephonyDomains.tenant'), visible: true, sortable: true },
      { key: 'language', label: t('telephonyDomains.language'), visible: true, sortable: true },
      { key: 'timezone', label: t('telephonyDomains.timezone'), visible: true, sortable: true },
      { key: 'countryCode', label: t('telephonyDomains.countryCode'), visible: true, sortable: true },
      { key: 'domainEnabled', label: t('telephonyDomains.enabled'), visible: true, sortable: true },
      { key: 'createdAt', label: t('telephonyDomains.created'), visible: true, sortable: true },
      { key: 'actions', label: t('telephonyDomains.actions'), visible: true, sortable: false }
    ];
  }, [t, i18n.language, refreshKey]);

  const formatDate = useCallback(
    (date: string) =>
      date ? new Date(date).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US') : '—',
    [i18n.language]
  );

  const getColumnRenderers = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (columns: Array<{ key: string; label: string; visible: boolean; sortable?: boolean }>): Array<{ key: string; label: string; visible: boolean; sortable: boolean; render?: (value: any, item: any) => React.ReactNode }> => {
      return columns.map(col => {
        const baseCol: { key: string; label: string; visible: boolean; sortable: boolean } = {
          key: col.key,
          label: col.label,
          visible: col.visible,
          sortable: Boolean(col.sortable ?? false)
        };
        if (baseCol.key === 'domainName') {
          return {
            ...baseCol,
            render: (value: string, item: Domain) => (
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">{value}</span>
                {item.isPrimary && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                    title={t('telephonyDomains.primaryDomain')}
                  >
                    <Shield className="h-2.5 w-2.5" />
                    {t('telephonyDomains.primary')}
                  </span>
                )}
              </div>
            )
          };
        }
        if (baseCol.key === 'domainEnabled') {
          return {
            ...baseCol,
            render: (value: boolean) => (
              <span
                className={`inline-flex items-center rounded-full px-2 py-1 text-xs ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
              >
                {value ? t('common.active') : t('common.inactive')}
              </span>
            )
          };
        }
        if (baseCol.key === 'serviceType') {
          return {
            ...baseCol,
            render: (value: string) => {
              const type = value || 'pbx';
              const styles: Record<string, string> = {
                pbx: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
                trunking: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
                hybrid: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300'
              };
              return (
                <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium uppercase ${styles[type] || styles.pbx}`}>
                  {type}
                </span>
              );
            }
          };
        }

        if (baseCol.key === 'createdAt') {
          return {
            ...baseCol,
            render: (value: string) => <div className="text-sm text-muted-foreground">{formatDate(value)}</div>
          };
        }

        if (baseCol.key === 'language') {
          return {
            ...baseCol,
            render: (value: string) => (
              <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs">
                {value || '—'}
              </span>
            )
          };
        }

        return baseCol;
      });
    },
    [t, formatDate]
  );

  return {
    availableColumns,
    getColumnRenderers
  };
}

